import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Factory, Network, ShoppingCart, Truck, Smartphone, Store, Users, Leaf, Globe2, ShieldCheck, Target, Sparkles, Calendar, CheckCircle, Mail, Phone, MapPin, ChevronDown, ChevronUp, Handshake } from "lucide-react";
import { useNavigate } from "react-router-dom";

// ================= SafeLink helpers =================
function extractHashId(href) {
  if (!href || href[0] !== '#') return null;
  const after = href.slice(1);
  const id = after.split(/[?&]/)[0].trim();
  return id || null;
}

const SafeLink = ({ href, className = "", children, onClick = (e: any) => { } }) => {
  const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  const handleClick = (e) => {
    e.preventDefault();
    onClick && onClick(e);
    if (href.startsWith('#')) {
      const id = extractHashId(href);
      if (id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.location.hash = href;
    }
  };
  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

// ============== SHARED UI ==============
const Section = ({ id = "", className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const Card = ({ className = "", children }) => (
  <div className={`rounded-xl bg-white border border-slate-200 shadow-sm ${className}`}>{children}</div>
);

const H2 = ({ children, kicker, center }) => (
  <div className={`mb-8 ${center ? "text-center" : ""}`}>
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-emerald-600">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
    {children}
  </span>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-slate-900 text-white px-3 py-1 rounded-full">
    {children}
  </span>
);

// ============== DATA ==============
const streams = [
  {
    key: "ondc",
    title: "ONDC Infra Rails",
    priceLabel: "Starts from ₹3,540",
    color: "from-emerald-600 to-teal-600",
    icon: Network,
    tagline: "Plug‑and‑play ONDC network SaaS",
    bullets: [
      { icon: Factory, text: "Brands / Manufacturers Commerce SaaS" },
      { icon: Truck, text: "Logistics Operators Transporters SaaS" },
      { icon: Smartphone, text: "Commerce Buyer App" },
      { icon: Store, text: "Commerce Seller App" },
      { icon: Smartphone, text: "Logistics Buyer App" },
      { icon: Store, text: "Logistics Seller App" },
    ],
  },
  {
    key: "dse",
    title: "Digital Social Empowerment",
    priceLabel: "From ₹60,000 / year",
    color: "from-emerald-500 to-teal-600",
    icon: Users,
    tagline: "Community‑led platforms for inclusive growth",
    bullets: [
      { icon: ShoppingCart, text: "Community e‑Marketplace Platform" },
      { icon: Truck, text: "Quick Service Delivery Platform" },
      { icon: Leaf, text: "Circularity Platform (Reverse Logistics)" },
    ],
  },
  {
    key: "federated",
    title: "Federated Enterprises",
    priceLabel: "Contact us",
    color: "from-sky-500 to-cyan-600",
    icon: Globe2,
    tagline: "Unified platforms for large networks",
    bullets: [
      { icon: Building2, text: "Unified Enterprise Commerce Platform" },
      { icon: Truck, text: "Unified Logistics Enterprise Platform" },
      { icon: Handshake, text: "Unified Distribution Agency Platform" },
    ],
  },
  {
    key: "advisory",
    title: "Advisory Services",
    priceLabel: "Contact us",
    color: "from-amber-500 to-orange-600",
    icon: Target,
    tagline: "Strategy, policy, and platform innovation",
    bullets: [
      { icon: Sparkles, text: "Business Model Innovation built on ONDC & DPIs" },
      { icon: ShieldCheck, text: "Embrace ESG through Platform Innovation" },
      { icon: Users, text: "Re‑imagine CSR with nation‑building initiatives" },
    ],
  },
  {
    key: "enterprise",
    title: "Enterprise Solutions",
    priceLabel: "Contact us",
    color: "from-rose-500 to-fuchsia-600",
    icon: Building2,
    tagline: "Custom DNE platforms for multimillion‑dollar use cases",
    bullets: [
      { icon: Building2, text: "Customize DNE platform for scale" },
      { icon: Leaf, text: "ESG platform for sustainability drive" },
    ],
  },
];

const faqs = [
  { q: "What stack is this site built on?", a: "Pure React + TailwindCSS + framer‑motion + lucide‑react, same as your Communities & EDA single‑file apps." },
  { q: "Can we enable forms now?", a: "The contact form is client‑side; wire it later to your API, Formspree, or Netlify Forms." },
  { q: "Can we split pages?", a: "Yes. This single file can be dropped into a router or exported as sections across routes." },
  { q: "Is pricing configurable?", a: "All pricing labels are driven by the streams[] data above—edit once, reflected everywhere." },
];

// ============== NAV ==============
// const Nav = () => {
//   const items = [
//     { href: "#home", label: "Home" },
//     { href: "#streams", label: "Streams" },
//     { href: "#solutions", label: "Solutions" },
//     { href: "#pricing", label: "Pricing" },
//     { href: "#events", label: "Events" },
//     { href: "#contact", label: "Contact" },
//     { href: "#faq", label: "FAQ" },
//   ];
//   const [open, setOpen] = useState(false);
//   return (
//     <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
//       <Section className="flex h-16 items-center justify-between">
//         <SafeLink href="#home" className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
//           <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-emerald-600 text-white font-black">D</span>
//           DIGIIT.AI
//         </SafeLink>
//         <nav className="hidden md:flex items-center gap-6 text-sm">
//           {items.map(i => (
//             <SafeLink key={i.href} href={i.href} className="text-slate-600 hover:text-slate-900">{i.label}</SafeLink>
//           ))}
//           <SafeLink href="#contact" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700">Talk to us <ArrowRight className="w-4 h-4"/></SafeLink>
//         </nav>
//         <button className="md:hidden p-2" onClick={()=>setOpen(v=>!v)} aria-label="Toggle menu">
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900"></span>
//         </button>
//       </Section>
//       {open && (
//         <div className="md:hidden border-t border-slate-200 bg-white">
//           <Section className="py-4 flex flex-col gap-3">
//             {items.map(i => (
//               <SafeLink key={i.href} href={i.href} onClick={()=>setOpen(false)} className="text-slate-700">{i.label}</SafeLink>
//             ))}
//             <SafeLink href="#contact" onClick={()=>setOpen(false)} className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700">Talk to us <ArrowRight className="w-4 h-4"/></SafeLink>
//           </Section>
//         </div>
//       )}
//     </header>
//   );
// };

const Nav = () => {
  const submenus = [
    // { href: "#home", label: "Home"},
    { href: "#streams", label: "Streams" },
    { href: "#solutions", label: "Solutions" },
    { href: "#pricing", label: "Pricing" },
    { href: "#events", label: "Events" },
    { href: "#contact", label: "Contact" },
    { href: "#faq", label: "FAQ" },
  ];

  const [openMobile, setOpenMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);

  return (    
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
      <Section id="one-digiit" className="flex flex-row h-12 w-full items-center justify-right">
        {/* Second row: submenus (desktop only) */}
        <div className="hidden md:block flex flex-row w-full items-center justify-right">
          <Section
            id="nav-sub"
            className="flex items-center gap-4 py-2 overflow-x-auto justify-end"
          >
            {submenus.map((i) => (
              <SafeLink
                key={i.href}
                href={i.href}
                className="text-sm text-slate-600 hover:text-slate-900 whitespace-nowrap"
              >
                {i.label}
              </SafeLink>
            ))}

            {/* CTA */}
            <SafeLink
              href="#contact"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
            >
              Talk to Us <ArrowRight className="w-4 h-4" />
            </SafeLink>
          </Section>
        </div>


        {/* Mobile menu button */}
        <button
          className="md:hidden p-2"
          onClick={() => setOpenMobile((v) => !v)}
          aria-label="Toggle menu"
        >
          <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
          <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
          <span className="block w-6 h-0.5 bg-slate-900"></span>
        </button>
      </Section>

      {/* Mobile dropdown */}
      {openMobile && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <Section id={''} className="py-4 flex flex-col gap-3">
            <div className="mt-4 border-t border-slate-200 pt-4">
              {submenus.map((i) => (
                <SafeLink
                  key={i.href}
                  href={i.href}
                  onClick={() => setOpenMobile(false)}
                  className="text-slate-600"
                >
                  {i.label}
                </SafeLink>
              ))}
            </div>
            <SafeLink
              href="#contact"
              onClick={() => setOpenMobile(false)}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
            >
              Talk to Us <ArrowRight className="w-4 h-4" />
            </SafeLink>
          </Section>
        </div>
      )}
    </header>
  );
};


// ============== HERO ==============
const Hero = () => (
  <div id="home" className="relative overflow-hidden bg-white text-slate-900">
    <div className="absolute inset-0 opacity-[0.06] pointer-events-none bg-[linear-gradient(0deg,transparent_24%,#000_25%,#000_26%,transparent_27%,transparent_74%,#000_75%,#000_76%,transparent_77%),linear-gradient(90deg,transparent_24%,#000_25%,#000_26%,transparent_27%,transparent_74%,#000_75%,#000_76%,transparent_77%)] bg-[length:24px_24px]" />
    <Section className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35 }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">DIGIIT.AI</span>
          <h1 className="mt-3 text-5xl md:text-6xl font-black leading-[1.05]">
            Cloud for <span className="underline decoration-emerald-400 decoration-8 underline-offset-8">Digital Native</span> Enterprises
          </h1>
          <p className="mt-4 text-lg md:text-xl text-slate-600 max-w-2xl">Transform SMBs and Enterprises with <b>platforms</b> connected to India’s <b>Digital Public Infrastructures</b> (ONDC, ULIP, OCEN, DSEP).</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <SafeLink href="#streams" className="inline-flex items-center gap-2 rounded-full border border-slate-900 px-5 py-3 font-semibold">Explore Streams <ArrowRight className="w-4 h-4" /></SafeLink>
            <SafeLink href="#contact" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-3 font-semibold hover:bg-emerald-700">Request a demo</SafeLink>
          </div>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> Weekly solution clinics</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Secure • Compliant • Scalable</span>
          </div>
        </motion.div>
        <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: 0.05 }}>
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Pill>Five Streams</Pill>
              <span className="text-slate-500">Where to start</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {streams.map(s => (
                <li key={s.key} className="rounded-xl p-4 border border-slate-200 hover:bg-slate-50 transition">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} grid place-items-center text-white mb-2`}>
                    {React.createElement(s.icon, { className: "w-5 h-5" })}
                  </div>
                  <div className="font-semibold text-slate-900">{s.title}</div>
                  <div className="mt-1 text-sm text-slate-600">{s.tagline}</div>
                  <div className="mt-2 text-xs text-slate-500">{s.priceLabel}</div>
                </li>
              ))}
            </ul>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

// ============== STREAMS ==============
const Streams = () => (
  <Section id="streams" className="py-16">
    <H2 kicker="Our Five Streams" center>Start small, scale to enterprise</H2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {streams.map(s => (
        <motion.div key={s.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3 }}>
          <Card className="p-6 h-full flex flex-col">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${s.color} grid place-items-center text-white`}>
                {React.createElement(s.icon, { className: "w-5 h-5" })}
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900">{s.title}</div>
                <div className="text-xs text-slate-500">{s.priceLabel}</div>
              </div>
            </div>
            <p className="mt-3 text-slate-600">{s.tagline}</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {s.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" /> {b.text}</li>
              ))}
            </ul>
            <div className="mt-5 flex gap-3 mt-auto">
              <SafeLink href={`#solutions?stream=${s.key}`} className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Learn more</SafeLink>
              <SafeLink href="#contact" className="rounded-full px-4 py-2 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">Request demo</SafeLink>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ============== SOLUTIONS ==============
const Solutions = () => (
  <Section id="solutions" className="py-16">
    <H2 kicker="Solutions" center>What you can launch with DIGIIT.AI</H2>
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6">
        <div className="font-semibold text-slate-900 mb-2">ONDC Infra Rails (starter pack)</div>
        <ul className="text-sm text-slate-700 space-y-2">
          <li>✔️ ONDC registry onboarding support</li>
          <li>✔️ Seller & Buyer reference apps (commerce & logistics)</li>
          <li>✔️ Transporters SaaS for fleet & route orchestration</li>
          <li>✔️ Webhooks, ledgers, settlement & policy guardrails</li>
        </ul>
      </Card>
      <Card className="p-6">
        <div className="font-semibold text-slate-900 mb-2">Digital Social Empowerment (community pack)</div>
        <ul className="text-sm text-slate-700 space-y-2">
          <li>✔️ Community e‑Marketplace with cohorts & chapters</li>
          <li>✔️ Q‑service delivery (hyperlocal to inter‑city)</li>
          <li>✔️ Circularity & reverse logistics workflows</li>
          <li>✔️ CSR/ESG reporting widgets</li>
        </ul>
      </Card>
      <Card className="p-6">
        <div className="font-semibold text-slate-900 mb-2">Federated Enterprises (network pack)</div>
        <ul className="text-sm text-slate-700 space-y-2">
          <li>✔️ Unified commerce, logistics, distribution agency layers</li>
          <li>✔️ API standards, catalogs, and settlement rails</li>
          <li>✔️ Observability: metrics, logs, traces dashboards</li>
          <li>✔️ SSO, roles & fine‑grained access control</li>
        </ul>
      </Card>
      <Card className="p-6">
        <div className="font-semibold text-slate-900 mb-2">Advisory & Enterprise Solutions</div>
        <ul className="text-sm text-slate-700 space-y-2">
          <li>✔️ Business Model Innovation & DPI adoption playbooks</li>
          <li>✔️ ESG platform design & carbon math integrations</li>
          <li>✔️ Custom DNE implementations for multi‑million use cases</li>
          <li>✔️ Program governance, audits, and training</li>
        </ul>
      </Card>
    </div>
  </Section>
);

// ============== PRICING ==============
const Pricing = () => (
  <Section id="pricing" className="py-16">
    <H2 kicker="Pricing" center>Transparent entry points</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {[{ name: "ONDC Infra Rails", price: "₹3,540+", desc: "Starter subscription; add-ons available" }, { name: "Digital Social Empowerment", price: "₹60,000/yr+", desc: "Community programs & cohorts" }, { name: "Advisory / Federated / Enterprise", price: "Contact us", desc: "Scope‑based, outcome‑linked" }].map((p) => (
        <Card key={p.name} className="p-6 text-center">
          <div className="text-sm uppercase tracking-wider text-slate-500">{p.name}</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900">{p.price}</div>
          <div className="mt-2 text-xs text-slate-500">{p.desc}</div>
          <SafeLink href="#contact" className="mt-5 inline-flex items-center justify-center rounded-full px-4 py-2 bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">Talk to sales</SafeLink>
        </Card>
      ))}
    </div>
  </Section>
);

// ============== EVENTS ==============
const Events = () => (
  <Section id="events" className="py-16">
    <H2 kicker="Events" center>Upcoming clinics & demos</H2>
    <div className="grid md:grid-cols-2 gap-4">
      {[{ date: "2025-09-05", title: "ONDC Seller Bootcamp (Coimbatore)" }, { date: "2025-09-12", title: "Women Entrepreneurs Demo Day (Chennai)" }, { date: "2025-09-20", title: "ESG Reverse Logistics Roundtable (Bengaluru)" }].map(e => (
        <Card key={e.title} className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Calendar className="w-4 h-4" /> {new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
          <div className="mt-2 font-semibold text-slate-900">{e.title}</div>
          <div className="mt-3 flex gap-2">
            <SafeLink href="#contact" className="rounded-full px-3 py-1.5 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">RSVP</SafeLink>
            <SafeLink href="#" className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Details</SafeLink>
          </div>
        </Card>
      ))}
    </div>
  </Section>
);

// ============== CONTACT ==============
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", city: "", interest: "ONDC Infra Rails", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { alert("Please fill name & email"); return; }
    setSubmitted(true);
  };
  return (
    <div id="contact" className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200">
      <Section className="py-16">
        <H2 kicker="Contact" center>Request a demo / proposal</H2>
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-2">
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Full name</label>
                <input name="name" value={form.name} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your name" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input name="email" value={form.email} onChange={onChange} type="email" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="+91‑" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Company</label>
                <input name="company" value={form.company} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your org" />
              </div>
              <div>
                <label className="text-sm text-slate-600">City</label>
                <input name="city" value={form.city} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your city" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Interest</label>
                <select name="interest" value={form.interest} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  {streams.map(s => <option key={s.key} value={s.title}>{s.title}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Message</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Tell us about your goals"></textarea>
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-3 items-center">
                <button className="rounded-full bg-emerald-600 text-white px-5 py-3 font-semibold hover:bg-emerald-700" type="submit">Submit</button>
                {submitted && <span className="text-sm text-green-600 inline-flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Submitted! We will get back to you soon.</span>}
              </div>
            </form>
          </Card>
          <div className="space-y-4">
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Reach us</div>
              <div className="mt-3 text-sm text-slate-600 flex flex-col gap-2">
                <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> hello@digiit.ai</span>
                <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> +91‑90000 00000</span>
                <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> Coimbatore • Chennai • Bengaluru</span>
              </div>
            </Card>
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Deck & Brochure</div>
              <div className="mt-2 text-sm text-slate-600">Use your browser's <b>Print → Save as PDF</b> to export an instant one‑pager.</div>
              <button onClick={() => window.print()} className="mt-4 rounded-full px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm font-semibold">Print / Save PDF</button>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

// ============== FAQ ==============
const FAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <Section id="faq" className="py-16">
      <H2 kicker="FAQ" center>Answers to common questions</H2>
      <div className="max-w-3xl mx-auto space-y-3">
        {faqs.map((f, i) => (
          <Card key={f.q} className="p-4">
            <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(open === i ? null : i)}>
              <div className="font-semibold text-slate-900">{f.q}</div>
              {open === i ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
            </button>
            {open === i && <div className="mt-2 text-sm text-slate-600">{f.a}</div>}
          </Card>
        ))}
      </div>
    </Section>
  );
};

// ============== FOOTER ==============
const Footer = () => (
  <footer className="bg-white border-t border-slate-200 text-slate-600">
    <Section className="py-10">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        <div>
          <div className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
            <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-slate-900 text-white font-black">D</span>
            DIGIIT.AI
          </div>
          <p className="mt-3 text-sm text-slate-500 max-w-sm">Cloud for Digital Native Enterprises connected with India's Digital Public Infrastructures.</p>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Quick Links</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#streams" className="hover:text-slate-900">Streams</SafeLink></li>
            <li><SafeLink href="#solutions" className="hover:text-slate-900">Solutions</SafeLink></li>
            <li><SafeLink href="#pricing" className="hover:text-slate-900">Pricing</SafeLink></li>
            <li><SafeLink href="#events" className="hover:text-slate-900">Events</SafeLink></li>
            <li><SafeLink href="#contact" className="hover:text-slate-900">Contact</SafeLink></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Legal</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#" className="hover:text-slate-900">Terms</SafeLink></li>
            <li><SafeLink href="#" className="hover:text-slate-900">Privacy</SafeLink></li>
          </ul>
        </div>
      </div>
      <div className="mt-8 border-t border-slate-200 pt-6 text-xs text-slate-500">© {new Date().getFullYear()} DIGIIT.AI. All rights reserved.</div>
    </Section>
  </footer>
);

// ============== DEFAULT EXPORT ==============
export default function DIGIITSite() {
  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <Hero />
      <Streams />
      <Solutions />
      <Pricing />
      <Events />
      <Contact />
      <FAQ />
      <Footer />
    </div>
  );
}
